import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Icon from "@/icons";
import { useRef, useEffect, useState } from "react";

export default function VoiceMember({ participant, isFocused, handleFocus, isSpeaker, isMuted, isDeafened, isStreaming, voice }: { participant: any, isFocused: boolean, handleFocus: any, isSpeaker: boolean, isMuted: boolean, isDeafened: boolean, isStreaming: boolean, voice: any }) {
    const streamingRef = useRef<HTMLVideoElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const observer = {
            videoTileDidUpdate: (tileState: any) => {
                if (tileState.isContent) {
                    if (tileState.boundExternalUserId === participant.id) {
                        if (streamingRef.current) {
                            voice?.meetingSessionRef?.current?.audioVideo.bindVideoElement(tileState.tileId, streamingRef.current);
                        }
                    }
                }
            }
        };

        voice?.meetingSessionRef?.current?.audioVideo.addObserver(observer);

        return () => {
            voice?.meetingSessionRef?.current?.audioVideo.removeObserver(observer);
        };
    }, [participant?.id, voice, streamingRef]);

    useEffect(() => {
        if (isStreaming && streamingRef.current) {
            const tiles = voice?.meetingSessionRef?.current?.audioVideo.getAllVideoTiles();
            const tile = tiles.find((tile: any) => tile.tileState.boundExternalUserId === participant.id);

            if (tile) {
                voice?.meetingSessionRef?.current?.audioVideo.bindVideoElement(tile.tileState.tileId, streamingRef.current);
            }
        }
    }, [isStreaming, participant?.id, voice, streamingRef]);

    const handleFullScreen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (streamingRef.current) {
            if (streamingRef.current.requestFullscreen) {
                streamingRef.current.requestFullscreen();
                //@ts-ignore
            } else if (streamingRef.current.webkitEnterFullscreen) {
                //@ts-ignore
                streamingRef.current.webkitEnterFullscreen();
                //@ts-ignore
            } else if (streamingRef.current.webkitRequestFullscreen) {
                //@ts-ignore
                streamingRef.current.webkitRequestFullscreen();
                //@ts-ignore
            } else if (streamingRef.current.msRequestFullscreen) {
                //@ts-ignore
                streamingRef.current.msRequestFullscreen();
            } else {
                alert("Your browser doesn't support fullscreen");
            }
            setIsFullScreen(true);
        }
    };

    const handleExitFullScreen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
            (document as any).msExitFullscreen();
        }
        setIsFullScreen(false);
    };

    return (
        <div
            key={participant.id}
            className={`relative group flex-col h-full inline-grid min-w-96 gap-2 rounded-lg overflow-hidden ${isFocused ? "min-h-96" : "sm:min-h-64 min-h-32"} ${isStreaming ? "" : "bg-brand-back"} w-full cursor-pointer ${isSpeaker ? "ring-2 ring-green-500" : ""}`}
            onClick={() => handleFocus(participant?.id)}
        >
            {!isStreaming && (
                <>
                    <div className="absolute group-hover:h-5 z-10 transition-all inset-x-0 bottom-0 h-8 bg-gradient-to-t from-brand-darkest opacity-75"></div>
                    <div className="absolute group-hover:h-5 z-10 transition-all inset-x-0 top-0 h-8 bg-gradient-to-b from-brand-darkest opacity-75"></div>
                </>
            )}

            <div className="hidden group-hover:absolute group-hover:flex top-2 left-8 z-10 p-2 text-white font-bold">{participant?.username}</div>
            <video
                autoPlay
                playsInline
                controls={false}
                ref={streamingRef}
                className={`absolute h-full w-full object-contain rounded-lg ${isFullScreen ? "z-20" : ""}`}
            />
            {!isStreaming && (
                <div className="items-center w-full relative justify-center flex h-full rounded-lg">
                    <Avatar className={`sm:w-32 h-20 w-20 sm:h-32 rounded-full bg-gray-600 items-center flex justify-center`}>
                        <AvatarImage draggable={false} className="rounded-full" src={participant?.profilePicture} />
                        <AvatarFallback className="text-lg">{participant?.username[0]}</AvatarFallback>
                    </Avatar>
                </div>
            )}

            <div className="absolute z-40 flex gap-2 bottom-2 right-2 p-2 text-white font-bold">
                {isFullScreen && (
                    <button onClick={handleExitFullScreen} className="text-white font-bold bg-black/50 p-2 rounded-full">
                        <Icon name="exit-fullscreen" size="12" />
                    </button>
                )}

                {!isFullScreen && isMuted && (
                    <div className="text-gray-300/75 font-bold bg-black/50 p-2 rounded-full">
                        <Icon name="voice-muted" size="12" />
                    </div>
                )}

                {!isFullScreen && isDeafened && (
                    <div className="text-gray-300/75 font-bold bg-black/50 p-2 rounded-full">
                        <Icon name="voice-deafened" size="12" />
                    </div>
                )}

                {isStreaming && !isFullScreen && (
                    <button onClick={handleFullScreen} className="text-white font-bold bg-black/50 p-2 rounded-full">
                        <Icon name="fullscreen" size="12" />
                    </button>
                )}
            </div>
        </div>
    );
}
